/**
 * The company's own master data. An XRechnung carries the seller's identity, address, VAT id,
 * a contact and the payment account — EN 16931 makes all of them mandatory, and FORQ has never
 * needed to know any of it before (an invoice only ever carried customer number and name).
 *
 * Asked for, never guessed: this ends up on a legally mandated document. Die WERTE stehen in der
 * Grundkonfiguration des Mandanten (company_profiles.profile); diese Datei haelt nur noch die Form
 * und die Pruefungen. Bis 09/2026 lagen sie zusaetzlich im localStorage -- damit hing die USt-IdNr.
 * auf der XRechnung am Browser des Mitarbeiters statt am Mandanten. Abbildung: apps/web/src/finance/companySettings.ts.
 */
export interface SellerSettings {
  readonly name: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly countryCode: string;
  /** USt-IdNr. (BT-31), e.g. DE123456789. */
  readonly vatId: string;
  /** Steuernummer — used when no USt-IdNr. exists. */
  readonly taxNumber: string;
  readonly contactName: string;
  readonly contactPhone: string;
  readonly contactEmail: string;
  readonly iban: string;
  readonly bic: string;
  readonly bankName: string;
}

export const emptySellerSettings: SellerSettings = {
  name: '', street: '', postalCode: '', city: '', countryCode: 'DE',
  vatId: '', taxNumber: '', contactName: '', contactPhone: '', contactEmail: '',
  iban: '', bic: '', bankName: '',
};


/** German USt-IdNr.: DE + 9 digits. Other EU forms differ, so only DE is asserted strictly. */
export function isValidVatId(vatId: string): boolean {
  const value = vatId.replace(/\s/g, '').toUpperCase();
  if (value.startsWith('DE')) return /^DE\d{9}$/.test(value);
  return /^[A-Z]{2}[0-9A-Z]{2,12}$/.test(value);
}

/** IBAN: structure plus the mod-97 check — a typo'd IBAN means the money does not arrive. */
export function isValidIban(iban: string): boolean {
  const value = iban.replace(/\s/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[0-9A-Z]{10,30}$/.test(value)) return false;
  const rearranged = value.slice(4) + value.slice(0, 4);
  const digits = [...rearranged]
    .map((character) => (/[A-Z]/.test(character) ? String(character.charCodeAt(0) - 55) : character))
    .join('');
  let remainder = 0;
  for (const digit of digits) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
}

/** What is still missing before an e-invoice may be produced, in words the user can act on. */
export function sellerSettingsIssues(settings: SellerSettings): readonly string[] {
  const issues: string[] = [];
  if (!settings.name.trim()) issues.push('Firmenname fehlt.');
  if (!settings.street.trim()) issues.push('Straße fehlt.');
  if (!settings.postalCode.trim()) issues.push('PLZ fehlt.');
  if (!settings.city.trim()) issues.push('Ort fehlt.');
  if (!/^[A-Z]{2}$/.test(settings.countryCode)) issues.push('Ländercode fehlt (z. B. DE).');
  if (!settings.vatId.trim() && !settings.taxNumber.trim()) {
    issues.push('USt-IdNr. oder Steuernummer fehlt – eine der beiden ist Pflicht.');
  } else if (settings.vatId.trim() && !isValidVatId(settings.vatId)) {
    issues.push(`USt-IdNr. „${settings.vatId}" hat kein gültiges Format (z. B. DE123456789).`);
  }
  // XRechnung requires a seller contact (BG-6).
  if (!settings.contactName.trim()) issues.push('Ansprechpartner fehlt (in der XRechnung Pflicht).');
  if (!isValidEmail(settings.contactEmail)) issues.push('E-Mail des Ansprechpartners fehlt oder ist ungültig.');
  if (!settings.contactPhone.trim()) issues.push('Telefonnummer des Ansprechpartners fehlt.');
  if (!settings.iban.trim()) issues.push('IBAN fehlt – ohne Zahlungskonto keine E-Rechnung.');
  else if (!isValidIban(settings.iban)) issues.push(`IBAN „${settings.iban}" ist nicht prüfziffernrichtig.`);
  return issues;
}

export function isSellerConfigured(settings: SellerSettings): boolean {
  return sellerSettingsIssues(settings).length === 0;
}

